
import { Component, Vue, Watch, Prop } from 'vue-property-decorator'
import { ListLeaks, BatchAssignRepairDept} from '@/api/leaks'
import { ILeakPoint, IAllDeptInfo, LeakStatus, ISimpleLeakPoint, DeptType } from '@/api/types'
import AMapLoader from '@amap/amap-jsapi-loader'
import { GetLeakStatusColor, GetLeakMapIcon } from '@/utils/statusUi'
import { HasPer, IsMobile } from '@/utils/check'
import { getCurrentPosition } from '@/utils/coord'


// 添加全局变量类型声明
declare global {
  interface Window {
    AMap: any
    AMapLoader: any
  }
}

// 定义状态映射配置
interface StatusMappingConfig {
  value: LeakStatus [] // 值
  label: string // 显示标签
  color: string // 状态指示器颜色
  iconKey: LeakStatus // 图标键名
}

@Component({
  name: 'LeakMapList',
})
export default class LeakMapList extends Vue {
  @Prop({ default: null }) private deptList!: IAllDeptInfo[] | []
  @Prop({ default: false }) private shouldReload!: boolean
  @Prop({ default: () => ['待分配', '待认领', '维修中', '已处理'] }) private defaultActiveLayers!: string[]
  @Prop({ default: false }) private autoZoom!: boolean
  @Prop({ default: () => [] }) private visibleStatusOptions!: string[] // 控制哪些状态选项可见，默认全部可见

  // 状态映射配置
  private statusMappings: StatusMappingConfig[] = [
    { value: [LeakStatus.Unreported], label: '未上报', color: GetLeakStatusColor(LeakStatus.Unreported), iconKey: LeakStatus.Unreported },
    { value: [LeakStatus.NeedAssignRepairDept], label: '待分配', color: GetLeakStatusColor(LeakStatus.NeedAssignRepairDept), iconKey: LeakStatus.NeedAssignRepairDept },
    { value: [LeakStatus.NeedAssignRepairUser], label: '待认领', color: GetLeakStatusColor(LeakStatus.NeedAssignRepairUser), iconKey: LeakStatus.NeedAssignRepairUser },
    { value: [LeakStatus.Repairing], label: '维修中', color: GetLeakStatusColor(LeakStatus.Repairing), iconKey: LeakStatus.Repairing },
    { value: [LeakStatus.Repaired, LeakStatus.CantRepair, LeakStatus.NoNeedRepair], label: '已处理', color: GetLeakStatusColor(LeakStatus.Repaired), iconKey: LeakStatus.Repaired }
  ]

  // 状态映射配置 Map
  private statusMappingMap: Map<string, StatusMappingConfig> = new Map(
    this.statusMappings.map(mapping => [mapping.label, mapping])
  )

  // 漏点数据相关
  private list: ISimpleLeakPoint[] = []
  private total = 0
  private mapLoading = false
  private mapLoadingError = ''
  private mapRetryCount = 0
  private mapMaxRetries = 3
  private mapInitializeTimeout = 500
  private geolocation: any = null
  private listQuery = {
    status: [] as number[], // 所有状态
    keywords: [''] as string[],
    addressKeywords: [''] as string[],
    createTimeStart: '',
    createTimeEnd: '',
    reportTimeStart: '',
    reportTimeEnd: '',
    simple: true, // 使用从外部传入的参数
  }

  // 地图相关
  private map: any = null
  private markers: any[] = []
  private rectSelect: any = null // 矩形选择工具
  private rectSelectLoading = false // 矩形选择工具加载状态
  private isRectSelectMode = false // 框选模式状态
  private isSearchPanelCollapsed = false // 搜索面板折叠状态
  private isToolsPanelCollapsed = false // 工具面板折叠状态
  private selectedLeak: ISimpleLeakPoint| null = null
  private showLeakInfo = false
  private labelLayer: any = null

  // 批量操作相关
  private multipleSelection: ILeakPoint[] = []
  private showBatchOperations = false
  private selectedDeptId = ''

  // 筛选条件相关
  private selectedYear: number | null = new Date().getFullYear()
  private reportMonth: string | null = null
  private activeLayers: string[] = [] // 默认的活动图层
  private assignableLeaks: ILeakPoint[] = []

  // 部门相关
  private batchAssignDeptId = ''
  private batchAssignLoading = false

  // 移动端弹窗相关
  private showMobileSearchDialog = false
  private showMobileToolsDialog = false

  // PC端弹窗相关
  private showPCSearchDialog = false
  private showPCToolsDialog = false

  private isLocating = false

  // 计算属性
  get isMobile() {
    return IsMobile()
  }

  get yearOptions(): number[] {
    const currentYear = new Date().getFullYear()
    const years: number[] = []
    for (let i = 0; i < 10; i++) {
      years.push(currentYear - i)
    }
    return years
  }

  get hasBatchOperationPermission() {
    return HasPer(['漏点管理', '上帝']) && !this.isMobile
  }

  get hasCreatePermission() {
    return HasPer(['漏点管理', '漏点检测', '上帝'])
  }

  get repairDeptList(): Array<{ id: string, name: string }> {
    // 从deptList筛选出维修部门
    if (!this.deptList || !Array.isArray(this.deptList)) {
      return []
    }

    const repairDepts: Array<{ id: string, name: string }> = []

    const findRepairDepts = (deptInfos: IAllDeptInfo[]) => {
      for (const deptInfo of deptInfos) {
        // 检查当前部门是否为维修部门
        if (deptInfo.dept && deptInfo.dept.type === DeptType.Repair) {
          repairDepts.push({
            id: String(deptInfo.dept.id),
            name: deptInfo.dept.name
          })
        }
      }
    }

    findRepairDepts(this.deptList)
    return repairDepts
  }

  get canPerformBatchOperation(): boolean {
    this.updateAssignableLeaks()
    return this.assignableLeaks.length > 0 && !!this.selectedDeptId
  }

  // 更新可分配的漏点
  private updateAssignableLeaks() {
    if (!this.multipleSelection) {
      this.assignableLeaks = []
      return
    }
    this.assignableLeaks = this.multipleSelection.filter(item => item && item.leakStatus === LeakStatus.NeedAssignRepairDept)
  }

  get keywordInput() {
    if (this.listQuery && this.listQuery.keywords && this.listQuery.keywords.length > 0) {
      return this.listQuery.keywords[0] || ''
    }
    return ''
  }

  set keywordInput(value: string) {
    if (!this.listQuery) {
      this.initListQuery()
    }
    if (!this.listQuery.keywords || !Array.isArray(this.listQuery.keywords)) {
      this.listQuery.keywords = []
    }
    this.listQuery.keywords[0] = value
  }

  get addressInput() {
    if (this.listQuery && this.listQuery.addressKeywords && this.listQuery.addressKeywords.length > 0) {
      return this.listQuery.addressKeywords[0] || ''
    }
    return ''
  }

  set addressInput(value: string) {
    if (!this.listQuery) {
      this.initListQuery()
    }
    if (!this.listQuery.addressKeywords || !Array.isArray(this.listQuery.addressKeywords)) {
      this.listQuery.addressKeywords = []
    }
    this.listQuery.addressKeywords[0] = value
  }

  // 生命周期方法
  created() {
    // 设置默认选中图层
    if (this.defaultActiveLayers && this.defaultActiveLayers.length > 0) {
      // 使用父组件传入的默认图层
      this.activeLayers = this.defaultActiveLayers
    } else {
      // 默认选中除未上报外的所有图层
      this.activeLayers = []
    }

    // 其他代码不变
    this.ensureReactivePropertiesInitialized()

    // 设置默认年度筛选（当前年份）
    const currentYear = new Date().getFullYear()
    this.selectedYear = currentYear
    this.listQuery.createTimeStart = `${currentYear}-01-01T00:00:00Z`
    this.listQuery.createTimeEnd = `${currentYear}-12-31T23:59:59Z`

    this.getLeakList()
    window.addEventListener('resize', this.handleResize)
  }

  // 确保所有响应式属性都被正确初始化
  private ensureReactivePropertiesInitialized() {
    // 确保所有在模板中使用的属性都被初始化
    if (!this.multipleSelection) this.multipleSelection = []
    if (this.mapLoading === undefined) this.mapLoading = false
    if (this.showBatchOperations === undefined) this.showBatchOperations = false
    if (this.showLeakInfo === undefined) this.showLeakInfo = false
    if (this.selectedYear === undefined) this.selectedYear = new Date().getFullYear()
    if (this.reportMonth === undefined) this.reportMonth = null
    if (this.showMobileSearchDialog === undefined) this.showMobileSearchDialog = false
    if (this.showMobileToolsDialog === undefined) this.showMobileToolsDialog = false
    if (this.showPCSearchDialog === undefined) this.showPCSearchDialog = false
    if (this.showPCToolsDialog === undefined) this.showPCToolsDialog = false

    // 确保 listQuery 已经初始化
    if (!this.listQuery) {
      this.initListQuery()
    } else {
      if (!this.listQuery.keywords || !Array.isArray(this.listQuery.keywords) || this.listQuery.keywords.length === 0) {
        this.listQuery.keywords = ['']
      }
      if (!this.listQuery.addressKeywords || !Array.isArray(this.listQuery.addressKeywords) || this.listQuery.addressKeywords.length === 0) {
        this.listQuery.addressKeywords = ['']
      }
    }

    this.updateAssignableLeaks()
  }

  mounted() {
    // 确保所有响应式属性已初始化
    this.ensureReactivePropertiesInitialized()

    // 在组件挂载后初始化地图
    this.$nextTick(() => {
      this.initMap()

      // 添加窗口调整大小事件监听器
      window.addEventListener('resize', this.handleWindowResize)
    })
  }

  beforeDestroy() {
    // 移除事件监听器
    window.removeEventListener('resize', this.handleWindowResize)

    // 清理地图资源
    this.cleanupMapResources()
  }

  // 初始化高德地图
  private initMap() {
    if (this.map) {
      console.log('地图已经初始化，跳过初始化过程')
      return
    }

    const container = document.getElementById('leak-map-container')
    if (!container) {
      console.error('地图容器未找到，无法初始化地图')
      this.mapLoadingError = '地图容器不存在'
      return
    }

    console.log('开始初始化地图...')
    this.mapLoading = true
    this.mapLoadingError = ''

    try {

      // 加载高德地图
      AMapLoader.load({
        key: process.env.VUE_APP_AMAP_KEY || "a4cde5aaa8a2a5819e1f901742e9cdf1", // Web端开发者Key
        version: "2.0", // JSAPI版本
        plugins: [
          'AMap.Scale',
          'AMap.ToolBar',
          'AMap.MouseTool',
          'AMap.Geolocation'
        ]
      }).then((AMap) => {
        console.log('高德地图加载成功')
        // 保存AMap对象以便在组件其他地方使用
        window.AMap = AMap

        // 创建地图实例
        this.map = new AMap.Map('leak-map-container', {
          zoom: 10,
          center: [116.397455, 39.909187], // 杭州中心点
          viewMode: '2D',
          resizeEnable: true,
          mapStyle: 'amap://styles/normal',
          zooms: [3, 20],
          defaultCursor: 'default',
          jogEnable: false,
          pitchEnable: false,
          rotateEnable: false
        })

        this.geolocation = new AMap.Geolocation({
          enableHighAccuracy: true, // 是否使用高精度定位
          timeout: 10000, // 超时时间
          zoomToAccuracy: true, // 定位成功后调整地图视野
          buttonPosition: 'RB', // 将定位按钮移到左下角
          buttonOffset: new AMap.Pixel(15, 100), // 调整按钮偏移量，避免与比例尺重叠
          showButton: false, // 显示默认的定位按钮
          panToLocation: true, // 自动移动到定位位置
          maximumAge: 0 // 不缓存定位结果
        })

        this.isLocating = false

        this.map.addControl(this.geolocation)

        // 添加地图控件
        this.map.addControl(new AMap.Scale({
          position: 'LB' // 比例尺保持在左下角
        }))

        this.map.addControl(new AMap.ToolBar({
          position: 'LB', // 将工具栏移到左下角
          offset: new AMap.Pixel(10, 10) // 保持原有的偏移量
        }))

        let successCallback = (result: any) => {
          this.isLocating = false
          this.map.setCenter(result.position)
        }

        let errorCallback = (result: any) => {
          this.isLocating = false
        }


        if (!this.isLocating) {
          this.isLocating = true
          getCurrentPosition(this, this.geolocation, successCallback, errorCallback)
          // this.geolocation.getCurrentPosition((status: any, result: any) => {
          //   if (status === 'complete' && result.info === 'SUCCESS') {
          //     console.log('定位成功', result.position)
          //     this.map.setCenter(result.position)
          //   }
          //   this.isLocating = false
          // })
        }

        // 添加地图完成事件监听
        this.map.on('complete', () => {
          console.log('地图初始化完成，开始渲染漏点')
          this.renderLeakMarkers()

          // 在地图完成加载后初始化鼠标工具
          console.log('地图加载完成，准备初始化鼠标工具')
          setTimeout(() => {
            this.setMouseTool()

            // 如果renderLeakMarkers中的调整视图失败，这里再次尝试
            if (this.autoZoom && this.markers && this.markers.length > 0) {
              try {
                this.adjustMapViewToLeaks()
              } catch (error) {
                console.error('地图完成加载后自动调整视图失败:', error)
              }
            }
          }, 1000) // 延迟1秒确保地图完全就绪
        })

        // 添加地图点击事件
        this.map.on('click', () => {
          // 点击地图时隐藏漏点信息面板
          this.showLeakInfo = false
        })


        this.mapLoading = false
      }).catch((error) => {
        console.error('高德地图API加载失败', error)
        this.mapLoadingError = '地图API加载失败'
        this.mapLoading = false
        this.$message.error('地图加载失败，请刷新页面重试')
      })
    } catch (error) {
      console.error('地图初始化失败', error)
      this.mapLoadingError = '地图初始化失败'
      this.$message.error('地图初始化失败，请刷新页面重试')
      this.mapLoading = false
    }
  }

  // 监听属性变化
  @Watch('shouldReload')
  private async onShouldReloadChange() {
    if (this.shouldReload) {
      try {
        await this.getLeakList()
        // 确保地图已加载
        if (!this.map) {
          this.initMap()
        } else {
          this.renderLeakMarkers()
        }
      } catch (err) {
        console.error('重新加载数据失败:', err)
        this.$message.error('重新加载数据失败')
      } finally {
        this.$emit('update:shouldReload', false)
        this.$emit('reload-done')
      }
    }
  }

  @Watch('list')
  private onListChange() {
    if (this.map) {
      this.renderLeakMarkers()
      // renderLeakMarkers 方法中已经包含了对 adjustMapViewToLeaks 的调用
    }
  }

  @Watch('activeLayers')
  private onActiveLayersChange() {
    if (this.map) {
      this.renderLeakMarkers()
    }
  }

  // 方法
  private handleResize() {
    if (this.map) {
      this.map.resize()
    }
  }

  private formatDate(date: string | undefined | null): string {
    if (!date) {
      return '暂无日期'
    }

    try {
      const d = new Date(date)
      if (isNaN(d.getTime())) {
        return '日期格式错误'
      }

      const year = d.getFullYear()
      const month = (d.getMonth() + 1).toString().padStart(2, '0')
      const day = d.getDate().toString().padStart(2, '0')

      return `${year}-${month}-${day}`
    } catch (error) {
      console.error('日期格式化错误', error)
      return '日期格式错误'
    }
  }

  // 获取漏点列表数据
  private async getLeakList() {
    this.mapLoading = true

    try {
      // 确保 listQuery 对象结构完整
      if (!this.listQuery) {
        console.log('listQuery is undefined');
        this.initListQuery();
      } else {
        // 修复：确保数组存在并且有效，同时保留用户输入的值
        if (!this.listQuery.keywords || !Array.isArray(this.listQuery.keywords)) {
          this.listQuery.keywords = [''];
        }
        if (!this.listQuery.addressKeywords || !Array.isArray(this.listQuery.addressKeywords)) {
          this.listQuery.addressKeywords = [''];
        }
      }

      const { data } = await ListLeaks({
        ...this.listQuery
      })

      this.list = data.items || []
      this.total = data.total || 0

      console.log(`获取到 ${this.list.length} 个漏点`)

      // 记录日志以帮助调试
      if (this.list.length > 0) {
        console.log('漏水点数据列表长度:', this.list.length)
        // 查看是否所有行都有相同的问题
        this.list.forEach((item, index) => {
          if (index < 3) { // 只输出前3条记录避免日志过多
            console.log(`第${index+1}条记录:`, JSON.stringify({
              id: item.id,
              leakStatus: item.leakStatus,
              detectionPosition: item.location ? {
                longitude: item.location.longitude,
                latitude: item.location.latitude
              } : null
            }))
          }
        })
      }
    } catch (error) {
      console.error('获取漏点列表失败', error)
      this.$message.error('获取漏点列表失败')
    } finally {
      this.mapLoading = false
    }
  }

  // 初始化 listQuery 对象
  private initListQuery() {
    const currentYear = new Date().getFullYear()

    // 从 statusMappings 中提取所有状态值
    const statusValues: number[] = this.statusMappings.map(
      mapping => mapping.value).flat()

    this.listQuery = {
      status: statusValues, // 使用所有状态值
      keywords: [''] as string[],
      addressKeywords: [''] as string[],
      createTimeStart: `${currentYear}-01-01T00:00:00Z`,
      createTimeEnd: `${currentYear}-12-31T23:59:59Z`,
      reportTimeStart: '',
      reportTimeEnd: '',
      simple: true,
    }
  }

  // 在地图上渲染漏点标记
  private renderLeakMarkers() {
    // 检查地图是否存在
    if (!this.map) {
      console.warn('地图实例不存在，无法渲染标记')
      return
    }

    // 检查漏点列表
    if (!this.list || !Array.isArray(this.list) || this.list.length === 0) {
      console.warn('漏点列表为空或无效')
      return
    }

    console.log('开始渲染漏点标记，共有', this.list.length, '个漏点')

    try {
      // 清除现有标记
      if (this.markers && this.markers.length > 0 && this.labelLayer) {
        try {
          // this.map.remove(this.markers)
          this.labelLayer.remove(this.markers)
        } catch (e) {
          console.error('清除现有标记失败:', e)
        }
        this.markers = []
      }

      // 按照当前选择的图层筛选漏点
      const filteredLeaks = this.list.filter(leak => {
        if (!leak) return false

        // 确保漏点有状态
        if (leak.leakStatus === undefined || leak.leakStatus === null) {
          console.warn('漏点状态未定义', leak)
          return false
        }

        // 确保漏点有位置信息
        const hasLocation = leak &&
                          leak.location &&
                          leak.location.longitude &&
                          leak.location.latitude

        if (!hasLocation) return false

        // 检查当前漏点状态是否在选定的活动图层中
        let shouldShow = false

        for (const layer of this.activeLayers) {
          const statusConfig = this.statusMappingMap.get(layer)
          if (statusConfig?.value.includes(leak.leakStatus)) {
            console.log('漏点状态:', leak.leakStatus, '符合条件', leak)
            shouldShow = true
              break
          }
        }

        return shouldShow
      })

      if (filteredLeaks.length === 0) {
        console.log('没有符合条件的漏点可以显示')
        return
      }

      console.log(`筛选后有 ${filteredLeaks.length} 个漏点可以显示`)

      if (!this.labelLayer) {
        this.labelLayer = new window.AMap.LabelsLayer({
          zooms: [3, 20],
          zIndex: 1000,
          collision: true,
          allowCollision: true,
        })
        this.map.add(this.labelLayer)
      }

      // 创建新标记
      const markersArray: any[] = []

      filteredLeaks.forEach(leak => {
        if (!leak|| !leak.location) return

        const longitude = leak.location.longitude
        const latitude = leak.location.latitude

        // 验证坐标有效性
        if (!longitude || !latitude || isNaN(Number(longitude)) || isNaN(Number(latitude))) {
          console.warn('漏点坐标无效:', leak.index, leak.location)
          return
        }

        const position = [longitude, latitude]

        try {

          // 获取对应的图标键名
          let iconKey = LeakStatus.Unreported // 默认图标键名

          // 查找对应的状态配置
          for (const statusConfig of this.statusMappings) {
            if (statusConfig.value.includes(leak.leakStatus)) {
              console.log('iconKey:', statusConfig.iconKey)
              console.log('leak.leakStatus:', leak.leakStatus)
              console.log('statusConfig.value:', statusConfig.value)
              iconKey = statusConfig.iconKey
              break
            }
          }

          const iconPath = GetLeakMapIcon(iconKey)

          console.log('iconPath:', iconPath)

          if (!iconPath) {
            console.error('无法找到对应状态的图标:', leak.leakStatus)
            return
          }

          // 创建标记
          const marker = new window.AMap.LabelMarker({
            name: leak.index || '',
            position: position,
            extData: { leak: leak },
            icon: {
              type: 'image',
              image: iconPath,
              size: [30, 30],
              anchor: 'bottom-center',
            },
            rank: 1,
            zIndex: 16,
          })

          // 添加点击事件
          marker.on('click', () => {
            this.selectedLeak = leak
            this.showLeakInfo = true
            // 向外部传递detail消息
            this.$emit('detail', leak.id)
          })

          markersArray.push(marker)
        } catch (err) {
          console.error('创建标记失败', err, leak)
        }
      })

      if (markersArray.length === 0) {
        console.warn('没有有效的标记点可以添加到地图')
        return
      }

      console.log(`成功创建 ${markersArray.length} 个标记点`)

      // 添加标记到地图
      this.markers = markersArray

      this.labelLayer.add(markersArray)

      // 自动调整地图视图以显示所有漏点
      if (markersArray.length > 0) {
        this.adjustMapViewToLeaks()
      }
    } catch (err) {
      console.error('渲染标记时出错', err)
      this.$message.error('渲染地图标记失败，请刷新重试')
    }
  }

  // 渲染聚合标记
  private renderClusterMarker(context: any): any {
    if (!context) {
      console.warn('聚合标记上下文为空')
      return null
    }

    try {
      const count = context.count

      if (!count || count <= 0) {
        console.warn('聚合点数量无效:', count)
        return null
      }

      // 根据数量计算大小
      let size = 40
      if (count > 10) size = 50
      if (count > 50) size = 60

      // 创建聚合点标记
      const markerNode = document.createElement('div')
      markerNode.className = 'custom-marker-cluster'
      markerNode.style.backgroundColor = '#1890FF'
      markerNode.style.color = '#FFF'
      markerNode.style.borderRadius = '50%'
      markerNode.style.width = `${size}px`
      markerNode.style.height = `${size}px`
      markerNode.style.lineHeight = `${size}px`
      markerNode.style.textAlign = 'center'
      markerNode.style.fontSize = '16px'
      markerNode.innerHTML = count.toString()

      return markerNode
    } catch (error) {
      console.error('渲染聚合标记失败', error)
      // 返回默认的聚合标记
      const defaultNode = document.createElement('div')
      defaultNode.style.backgroundColor = '#1890FF'
      defaultNode.style.color = '#FFF'
      defaultNode.style.borderRadius = '50%'
      defaultNode.style.width = '40px'
      defaultNode.style.height = '40px'
      defaultNode.style.lineHeight = '40px'
      defaultNode.style.textAlign = 'center'
      defaultNode.innerHTML = '+'
      return defaultNode
    }
  }

  // 处理矩形选择
  private handleRectangleSelection(bounds: any) {
    if (!bounds || !this.markers || !Array.isArray(this.markers)) {
      console.error('矩形选择数据无效或标记数组未初始化')
      return
    }

    const selectedLeaks: ILeakPoint[] = []

    try {
      this.markers.forEach(marker => {
        if (!marker || !marker.getPosition || typeof marker.getPosition !== 'function') {
          return
        }

        const position = marker.getPosition()
        const extData = marker.getExtData && marker.getExtData()
        const leak = extData && extData.leak

        if (!position || !leak) {
          return
        }

        // 判断点是否在矩形内
        if (bounds.contains && bounds.contains(position)) {
          selectedLeaks.push(leak)
        }
      })

      if (selectedLeaks.length > 0) {
        this.multipleSelection = selectedLeaks
        this.updateAssignableLeaks()
        const assignableCount = this.assignableLeaks.length

        if (assignableCount > 0) {
          this.showBatchOperations = true
        } else {
          this.$message.warning('选中区域内没有可分配的漏点（待分配状态）')
        }
      } else {
        this.$message.info('没有选中任何漏点')
      }
    } catch (error) {
      console.error('处理矩形选择时出错:', error)
      this.$message.error('处理矩形选择时出错')
    }
  }

  // 清理地图资源的方法
  private cleanupMapResources() {
    console.log('清理地图资源...')
    try {
      // 如果处于框选模式，先退出框选模式
      if (this.isRectSelectMode) {
        this.exitRectSelectMode();
      }

      if (this.markers && this.markers.length > 0 && this.labelLayer) {
        this.labelLayer.remove(this.markers)
        this.markers = []
      }

      if (this.rectSelect) {
        if (typeof this.rectSelect.close === 'function') {
          this.rectSelect.close()
        }
        this.rectSelect = null
      }

      if (this.map) {
        this.map.destroy() // 销毁地图实例
        this.map = null
      }

      console.log('地图资源清理完成')
    } catch (error) {
      console.error('清理地图资源时出错', error)
    }
  }

  // 设置鼠标工具
  private setMouseTool() {
    if (!this.map) {
      console.warn('[DEBUG] 地图未加载，无法设置鼠标工具')
      return
    }

    try {
      console.log('[DEBUG] 开始初始化鼠标工具')

      // 确保AMap已加载
      if (!window.AMap) {
        console.error('[DEBUG] AMap未加载，无法创建MouseTool')
        this.$message.error('地图组件未完全加载，请刷新页面重试')
        return
      }

      // 销毁旧的MouseTool实例（如果存在）
      if (this.rectSelect) {
        try {
          // 移除所有事件监听
          this.rectSelect.off('draw');

          // 彻底关闭工具
          this.rectSelect.close(true);
          this.rectSelect = null;
        } catch (e) {
          console.error('[DEBUG] 清理旧的MouseTool实例失败:', e);
        }
      }

      // 创建全新的MouseTool实例
      this.rectSelect = new window.AMap.MouseTool(this.map);
      console.log('[DEBUG] 新的MouseTool创建成功:', this.rectSelect);
    } catch (error) {
      console.error('[DEBUG] 初始化鼠标工具失败:', error);
      this.$message.error('初始化选择工具失败，请刷新页面重试');
      this.rectSelect = null;
    }
  }

  // 切换矩形选择工具状态
  private toggleRectSelect() {
    if (this.isRectSelectMode) {
      // 如果当前是框选模式，则退出框选模式
      this.exitRectSelectMode();
    } else {
      // 如果当前不是框选模式，则进入框选模式
      this.enableRectSelect();
    }
  }

  // 退出矩形选择模式
  private exitRectSelectMode() {
    console.log('[DEBUG] 退出分配模式');

    this.rectSelectLoading = true;

    try {
      // 关闭矩形工具
      if (this.rectSelect) {
        this.rectSelect.close(true);
      }

      // 恢复地图状态
      if (this.map) {
        this.map.setStatus({dragEnable: true});
        this.map.setDefaultCursor('default');
      }

      // 更新状态
      this.isRectSelectMode = false;
      this.$message.info('已退出分配模式');
    } catch (error) {
      console.error('[DEBUG] 退出分配模式失败:', error);
      this.$message.error('退出分配模式失败');
    } finally {
      this.rectSelectLoading = false;
    }
  }

  // 启用矩形选择工具
  private enableRectSelect() {
    console.log('[DEBUG] 启用矩形选择工具');

    // 检查地图是否可用
    if (!this.map) {
      this.$message.error('地图未加载，无法启用选择工具');
      return;
    }

    // 如果已经在分配模式，则退出分配模式
    if (this.isRectSelectMode) {
      this.exitRectSelectMode();
      return;
    }

    // 设置加载状态
    this.rectSelectLoading = true;

    try {
      // 每次都重新初始化MouseTool以确保工具状态干净
      this.setMouseTool();

      // 直接激活矩形工具
      setTimeout(() => {
        if (this.rectSelect) {
          this.activateMouseToolRectangle();
          // 设置为框选模式
          this.isRectSelectMode = true;
        } else {
          this.$message.error('矩形选择工具初始化失败，请刷新页面重试');
          this.rectSelectLoading = false;
        }
      }, 300); // 短暂延迟确保工具初始化完成
    } catch (error) {
      console.error('[DEBUG] 启用矩形选择工具失败:', error);
      this.$message.error('启用选择工具失败，请刷新页面重试');
      this.rectSelectLoading = false;
    }
  }

  // 激活高德MouseTool的矩形绘制功能
  private activateMouseToolRectangle() {
    try {
      console.log('[DEBUG] 开始激活矩形绘制功能');

      // 先彻底关闭之前可能存在的工具实例
      if (this.rectSelect) {
        try {
          this.rectSelect.close(true);
        } catch (e) {
          console.error('[DEBUG] 关闭工具失败:', e);
        }
      }

      // 特别检查并确保地图对象有效
      if (!this.map) {
        console.error('[DEBUG] 地图对象不存在，无法激活绘图工具');
        this.rectSelectLoading = false;
        return;
      }

      if (!this.rectSelect) {
        console.warn('[DEBUG] MouseTool不存在，重新创建');
        this.rectSelect = new window.AMap.MouseTool(this.map);
      }

      // 添加绘图完成事件监听（避免重复绑定）
      this.rectSelect.off('draw');
      this.rectSelect.on('draw', (event: any) => {
        console.log('[DEBUG] 矩形绘制完成', event);

        if (event.obj) {
          const bounds = event.obj.getBounds();
          this.handleRectangleSelection(bounds);

          // 移除绘制的矩形
          this.map.remove(event.obj);

          // 如果仍在框选模式，重新激活矩形工具以便继续绘制
          if (this.isRectSelectMode) {
            this.activateMouseToolRectangle();
          }
        }
      });

      // 确保地图状态设置正确
      this.map.setStatus({dragEnable: false});
      this.map.setDefaultCursor('crosshair');

      // 直接调用鼠标工具的矩形功能
      console.log('[DEBUG] 调用rectangle方法');
      this.rectSelect.rectangle({
        strokeColor: '#1791fc',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#1791fc',
        fillOpacity: 0.2,
        strokeStyle: 'solid'
      });

      // 强制更新地图视图（有时需要这一步来刷新地图状态）
      this.map.render();

      this.$message.success('分配工具已启用，请在地图上拖动鼠标进行选择');
      this.rectSelectLoading = false;
    } catch (error) {
      console.error('[DEBUG] 激活矩形绘制功能失败:', error);
      this.$message.error('激活分配功能失败');
      this.rectSelectLoading = false;

      // 确保恢复地图状态
      if (this.map) {
        this.map.setStatus({dragEnable: true});
        this.map.setDefaultCursor('default');
      }
    }
  }

  // 处理筛选
  private handleFilter() {
    // 重置到第一页
    this.getLeakList()
  }

  private resetFilter() {
    const currentYear = new Date().getFullYear()

    // 从 statusMappings 中提取所有状态值，除了未上报
    const statusValues: number[] = []
    this.statusMappings.forEach(mapping => {
      if (Number(mapping.value) !== LeakStatus.Unreported) {
        const value = String(mapping.value)
        if (value.includes(',')) {
          // 如果是组合状态，拆分出所有数字
          value.split(',').forEach(val => {
            const numVal = Number(val)
            if (!isNaN(numVal)) {
              statusValues.push(numVal)
            }
          })
        } else {
          // 单一状态
          const numVal = Number(value)
          if (!isNaN(numVal)) {
            statusValues.push(numVal)
          }
        }
      }
    })

    // 确保使用有效数组初始化
    const emptyKeywords: string[] = ['']
    const emptyAddressKeywords: string[] = ['']

    this.listQuery = {
      status: statusValues, // 使用除了未上报外的所有状态值
      keywords: emptyKeywords,
      addressKeywords: emptyAddressKeywords,
      reportTimeStart: '',
      reportTimeEnd: '',
      createTimeStart: `${currentYear}-01-01T00:00:00Z`,
      createTimeEnd: `${currentYear}-12-31T23:59:59Z`,
      simple: true,
    }

    // 重置筛选UI状态
    this.reportMonth = null
    this.selectedYear = currentYear
    this.activeLayers = this.defaultActiveLayers

    this.getLeakList()
  }

  // 处理年份变化
  private handleYearChange(year: number | null) {
    if (year) {
      // 设置创建时间的年度范围
      this.listQuery.createTimeStart = `${year}-01-01T00:00:00Z`
      this.listQuery.createTimeEnd = `${year}-12-31T23:59:59Z`

      // 如果有上报月份，确保在年份范围内
      if (this.reportMonth) {
        const monthYear = new Date(this.reportMonth).getFullYear()
        if (monthYear !== year) {
          this.reportMonth = null
          this.listQuery.reportTimeStart = ''
          this.listQuery.reportTimeEnd = ''
        }
      }
    } else {
      // 清空年度筛选
      this.listQuery.createTimeStart = ''
      this.listQuery.createTimeEnd = ''
    }
  }

  // 处理月份变化
  private handleMonthChange(month: string | null) {
    if (month) {
      // 获取月份的年和月
      const date = new Date(month)
      const year = date.getFullYear()
      const monthNum = date.getMonth()

      // 计算月份的开始和结束时间
      const startDate = new Date(year, monthNum, 1)
      const endDate = new Date(year, monthNum + 1, 0)

      // 设置上报时间范围
      this.listQuery.reportTimeStart = `${startDate.toISOString().split('T')[0]}T00:00:00Z`
      this.listQuery.reportTimeEnd = `${endDate.toISOString().split('T')[0]}T23:59:59Z`

      // 如果选择的月份年份与当前筛选年份不同，更新年份筛选
      if (this.selectedYear !== year) {
        this.selectedYear = year
        this.listQuery.createTimeStart = `${year}-01-01T00:00:00Z`
        this.listQuery.createTimeEnd = `${year}-12-31T23:59:59Z`
      }
    } else {
      // 清空月份筛选
      this.listQuery.reportTimeStart = ''
      this.listQuery.reportTimeEnd = ''
    }
  }

  // 处理图层变化
  private handleLayerChange() {
    this.renderLeakMarkers()
  }

  // 处理查看详情
  private handleViewDetail() {
    if (this.selectedLeak) {
      this.$emit('detail', this.selectedLeak.id)
      this.showLeakInfo = false
    }
  }

  // 处理批量操作
  private async handleBatchOperation() {
    if (!this.hasBatchOperationPermission) {
      this.$message({
        message: '您没有批量操作的权限',
        type: 'error'
      })
      this.showBatchOperations = false
      return
    }

    this.updateAssignableLeaks()  // 确保assignableLeaks是最新的

    if (!this.assignableLeaks || this.assignableLeaks.length === 0) {
      this.$message({
        message: '没有可分配的漏点',
        type: 'warning'
      })
      return
    }

    if (!this.selectedDeptId) {
      this.$message({
        message: '请选择维修部门',
        type: 'warning'
      })
      return
    }

    try {
      const leakIds = this.assignableLeaks.map(item => item.id)

      await BatchAssignRepairDept({
        leakIds: leakIds,
        deptId: parseInt(this.selectedDeptId)
      })

      this.$message.success(`成功分配 ${leakIds.length} 个漏点`)

      // 重新加载数据
      await this.getLeakList()

    } catch (error) {
      console.error('批量分配失败', error)
      this.$message.error('批量分配失败')
    } finally {
      // 关闭对话框并清空选择
      this.showBatchOperations = false
      this.selectedDeptId = ''
      this.multipleSelection = []
      this.updateAssignableLeaks()
    }
  }

  // 处理窗口大小变化
  private handleWindowResize() {
    if (this.map) {
      this.map.resize()
    }

    // 检查移动端状态变化
    const wasMobile = this.isMobile
    // isMobile 是计算属性，会自动重新计算

    // 如果从桌面端切换到移动端，需要关闭PC端弹窗
    if (!wasMobile && this.isMobile) {
      this.showPCSearchDialog = false
      this.showPCToolsDialog = false

      // 如果处于矩形选择模式，退出
      if (this.isRectSelectMode) {
        this.exitRectSelectMode()
      }
    }

    // 如果从移动端切换到桌面端，关闭移动端弹窗
    if (wasMobile && !this.isMobile) {
      this.showMobileSearchDialog = false
      this.showMobileToolsDialog = false
    }
  }

  // 编辑漏点方法
  private editLeak(leak: any) {
    if (!leak || !leak.id) {
      this.$message.warning('漏点信息不完整，无法编辑')
      return
    }

    // 将编辑操作的逻辑放在这里
    console.log('编辑漏点', leak)
    this.$emit('edit-leak', leak)
  }

  // 清除批量选择
  private clearBatchSelection() {
    this.multipleSelection = []
    this.assignableLeaks = []
    this.batchAssignDeptId = ''
  }

  // 确认批量指派
  private async confirmBatchAssign() {
    if (!this.batchAssignDeptId) {
      this.$message.warning('请选择维修部门')
      return
    }

    this.batchAssignLoading = true

    try {
      const leakIds = this.assignableLeaks.map(item => item.id)

      // 调用批量指派API
      await BatchAssignRepairDept({
        leakIds: leakIds,
        deptId: parseInt(this.batchAssignDeptId)
      })

      this.$message.success('批量指派成功')
      this.showBatchOperations = false
      this.clearBatchSelection()

      // 询问用户是否退出框选模式
      if (this.isRectSelectMode) {
        this.$confirm('批量指派成功，是否退出分配模式？', '提示', {
          confirmButtonText: '退出',
          cancelButtonText: '继续分配',
          type: 'info'
        }).then(() => {
          this.exitRectSelectMode();
        }).catch(() => {
          // 用户选择继续分配，无需操作
        });
      }

      // 刷新漏点列表
      this.getLeakList()
    } catch (error) {
      console.error('批量指派失败', error)
      this.$message.error('批量指派失败，请重试')
    } finally {
      this.batchAssignLoading = false
    }
  }

  // 获取部门名称
  private getDeptName(deptId: string | number | undefined | null): string {
    if (deptId === undefined || deptId === null) return '未分配'

    const stringId = String(deptId)
    const dept = this.repairDeptList.find(d => d.id === stringId)
    return dept ? dept.name : '未知部门'
  }

  // 辅助方法：获取上报时间
  private getReportTime(leak: any): string | null {
    if (!leak) return null

    // 尝试从不同字段获取上报时间
    if (leak.reportTime) return leak.reportTime
    if (leak.detection && leak.detection.reportTime) return leak.detection.reportTime
    if (leak.createTime) return leak.createTime

    return null
  }

  // 处理对话框关闭
  private handleDialogClose() {
    this.clearBatchSelection();
    // 注意：这里不退出框选模式，让用户可以继续框选其他区域
  }

  // 切换搜索面板显示状态
  private toggleSearchPanel() {
    this.isSearchPanelCollapsed = !this.isSearchPanelCollapsed;

    // 面板状态变化后强制更新地图尺寸以适应
    this.$nextTick(() => {
      if (this.map) {
        this.map.resize();
      }
    });
  }

  // 切换工具面板显示状态
  private toggleToolsPanel() {
    this.isToolsPanelCollapsed = !this.isToolsPanelCollapsed;

    // 面板状态变化后强制更新地图尺寸以适应
    this.$nextTick(() => {
      if (this.map) {
        this.map.resize();
      }
    });
  }

  // 移动端搜索处理
  private handleMobileSearch() {
    this.handleFilter()
    this.showMobileSearchDialog = false
  }

  // 移动端工具应用
  private handleMobileToolsApply() {
    // 应用工具栏的设置
    this.handleLayerChange()
    this.showMobileToolsDialog = false
  }

  // PC端搜索处理
  private handlePCSearch() {
    this.handleFilter()
    this.showPCSearchDialog = false
  }

  // PC端工具应用
  private handlePCToolsApply() {
    // 应用工具栏的设置
    this.handleLayerChange()
    this.showPCToolsDialog = false
  }

  // 处理创建漏点
  private handleCreateLeak() {
    // 关闭移动端弹窗（如果打开）
    if (this.showMobileToolsDialog) {
      this.showMobileToolsDialog = false
    }

    // 关闭PC端弹窗（如果打开）
    if (this.showPCToolsDialog) {
      this.showPCToolsDialog = false
    }

    // 向父组件发送创建事件
    this.$emit('create')
  }

  private handleLocateToCurrentPosition() {
    // 关闭移动端弹窗（如果打开）
    if (this.isLocating) {
      return
    }


    if (this.showMobileToolsDialog) {
      this.showMobileToolsDialog = false
    }

    // 关闭PC端弹窗（如果打开）
    if (this.showPCToolsDialog) {
      this.showPCToolsDialog = false
    }

    if (!this.map || !this.geolocation) {
      this.$message.warning('地图组件未初始化，无法定位')
      return
    }

    // this.$message.info('正在获取您的位置...')
    this.isLocating = true

    let successCallback = (result: any) => {
      this.isLocating = false
      this.map.setCenter(result.position)
      this.$message.success('已定位到当前位置' + result.position + result.location_type + result.message)
    }

    let errorCallback = (result: any) => {
      this.isLocating = false
      this.$message.error('定位失败：' + result.info + result.message)
      console.error('定位失败', result)
    }

    getCurrentPosition(this, this.geolocation, successCallback, errorCallback)

    // this.geolocation.getCurrentPosition((status: any, result: any) => {
    //   if (status === 'complete' && (result.info === 'SUCCESS')) {
    //     // 定位成功，将地图中心设置为当前位置
    //     const position = result.position
    //     this.map.setCenter(position) // 设置缩放级别和中心点
    //     this.$message.success('已定位到当前位置' + result.position + result.location_type + result.message)
    //   } else {
    //     // 定位失败
    //     this.$message.error('定位失败：' + result.info + result.message)
    //     console.error('定位失败', result)
    //   }
    //   this.isLocating = false
    // })
  }

  private handleAssignLeak() {
    // 启用矩形选择模式
    this.enableRectSelect()
  }

  // PC端弹窗相关方法
  private openPCSearchDialog() {
    this.showPCSearchDialog = true
  }

  private closePCSearchDialog() {
    this.showPCSearchDialog = false
  }

  private applyPCSearch() {
    this.getLeakList()
    this.closePCSearchDialog()
    this.$message.success('搜索条件已应用')
  }

  private resetPCSearch() {
    const currentYear = new Date().getFullYear()
    this.listQuery = {
      status: [], // 不包含未上报(0)
      keywords: [''],
      addressKeywords: [''],
      createTimeStart: `${currentYear}-01-01T00:00:00Z`,
      createTimeEnd: `${currentYear}-12-31T23:59:59Z`,
      reportTimeStart: '',
      reportTimeEnd: '',
      simple: true,
    }
    this.getLeakList()
    this.$message.success('搜索条件已重置')
  }

  private openPCToolsDialog() {
    this.showPCToolsDialog = true
  }

  private closePCToolsDialog() {
    this.showPCToolsDialog = false
  }

  private applyPCTools() {
    this.getLeakList()
    this.closePCToolsDialog()
    this.$message.success('工具设置已应用')
  }

  private get hasAssignableLeaks() {
    return this.multipleSelection && this.multipleSelection.length > 0
  }

  // 根据漏点位置自动调整地图视图
  private adjustMapViewToLeaks() {
    // 如果不需要自动缩放或地图未初始化，则返回
    if (!this.autoZoom || !this.map) {
      return
    }

    try {
      // 使用markers数组代替list，因为markers已经是经过筛选的有效点
      if (!this.markers || this.markers.length === 0) {
        return
      }

      // 收集所有有效的位置点
      const validPositions: number[][] = []

      this.markers.forEach(marker => {
        try {
          if (marker && typeof marker.getPosition === 'function') {
            const position = marker.getPosition()
            if (position) {
              const lng = position.getLng()
              const lat = position.getLat()

              if (!isNaN(lng) && !isNaN(lat) &&
                  lng >= -180 && lng <= 180 &&
                  lat >= -90 && lat <= 90) {
                validPositions.push([lng, lat])
              }
            }
          }
        } catch (e) {
          console.warn('获取标记位置出错:', e)
        }
      })

      // 如果没有有效位置，则返回
      if (validPositions.length === 0) {
        console.warn('没有有效的标记位置数据')
        return
      }

      // 如果只有一个点，直接设置中心点和缩放级别
      if (validPositions.length === 1) {
        try {
          const position = validPositions[0]
          if (Array.isArray(position) && position.length === 2) {
            this.map.setZoomAndCenter(16, position)
          }
        } catch (err) {
          console.error('设置单点中心位置失败:', err)
        }
        return
      }

      // 创建边界对象
      try {
        // 先找到最小和最大的经纬度值
        let minLng = 180
        let maxLng = -180
        let minLat = 90
        let maxLat = -90

        validPositions.forEach(position => {
          if (Array.isArray(position) && position.length === 2) {
            const [lng, lat] = position
            minLng = Math.min(minLng, lng)
            maxLng = Math.max(maxLng, lng)
            minLat = Math.min(minLat, lat)
            maxLat = Math.max(maxLat, lat)
          }
        })

        // 检查计算出的边界是否有效
        if (minLng > maxLng || minLat > maxLat ||
            isNaN(minLng) || isNaN(maxLng) ||
            isNaN(minLat) || isNaN(maxLat)) {
          console.error('计算的边界无效')
          return
        }

        // 使用计算的边界创建AMap.Bounds对象
        const southWest = new window.AMap.LngLat(minLng, minLat)
        const northEast = new window.AMap.LngLat(maxLng, maxLat)
        const bounds = new window.AMap.Bounds(southWest, northEast)

        // 设置地图视图以显示所有点
        this.map.setBounds(bounds, {
          padding: [50, 50, 50, 50]  // 边界周围的内边距
        })

        console.log('地图视图已根据漏点位置自动调整')
      } catch (error) {
        console.error('创建或设置地图边界失败:', error)

        // 备选方案：如果边界计算失败，尝试使用第一个有效点作为中心点
        if (validPositions.length > 0) {
          try {
            const position = validPositions[0]
            if (Array.isArray(position) && position.length === 2) {
              this.map.setZoomAndCenter(12, position)
              console.log('使用备选方案设置地图中心点')
            }
          } catch (err) {
            console.error('设置备选中心点失败:', err)
          }
        }
      }
    } catch (error) {
      console.error('调整地图视图失败', error)
    }
  }

  // 根据 visibleStatusOptions 筛选状态映射配置
  get filteredStatusMappings(): StatusMappingConfig[] {
    // 如果 visibleStatusOptions 为空，则显示全部选项
    if (!this.visibleStatusOptions || this.visibleStatusOptions.length === 0) {
      return this.statusMappings
    }

    // 否则仅显示 visibleStatusOptions 中包含的选项
    return this.statusMappings.filter(status =>
      this.visibleStatusOptions.includes(status.label)
    )
  }

  // 在地图上绘制矩形选择区域
  private handleRectSelect() {
    if (!this.map) return
    this.isRectSelectMode = !this.isRectSelectMode

    if (this.isRectSelectMode) {
      this.enableRectSelect()
    } else {
      this.exitRectSelectMode()
    }
  }
}
